import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Spin, Tag, Space, Typography, Image, List } from 'antd';
import PortalLayout from './PortalLayout';
import PortalErrorPage from './PortalErrorPage';
import { portalApi, PortalWorkOrderView } from '../../services/api/portalApi';

const { Text } = Typography;

const STATUS_COLOR: Record<string, string> = {
  new: 'default',
  created: 'default',
  scheduled: 'blue',
  in_progress: 'gold',
  waiting_parts: 'orange',
  waiting_approval: 'purple',
  completed: 'green',
  cancelled: 'red',
};

const PortalWorkOrderPage: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const [data, setData] = useState<PortalWorkOrderView | null>(null);
  const [error, setError] = useState<'not_found' | 'rate_limited' | 'unknown' | null>(null);

  useEffect(() => {
    if (!token) return;
    portalApi.getWorkOrder(token).then(setData).catch((err) => {
      const status = err?.response?.status;
      setError(status === 404 ? 'not_found' : status === 429 ? 'rate_limited' : 'unknown');
    });
  }, [token]);

  if (error) return <PortalErrorPage variant={error} />;
  if (!data) return (
    <PortalLayout title="Loading…" shopName="CNF Auto">
      <div style={{ textAlign: 'center', padding: 24 }}><Spin /></div>
    </PortalLayout>
  );

  const wo = data.work_order;
  const v = data.vehicle;
  const vehicleStr = [v.year, v.make, v.model].filter(Boolean).join(' ');

  return (
    <PortalLayout
      title="Work Order Progress"
      shopName="CNF Auto"
      subtitle={vehicleStr}
    >
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        <Space>
          <Text>Status:</Text>
          <Tag color={STATUS_COLOR[wo.status] ?? 'default'}>{wo.status?.toUpperCase()}</Tag>
        </Space>
        <List
          bordered
          dataSource={data.line_items}
          renderItem={(item: any) => (
            <List.Item>
              <Space direction="vertical" size={2} style={{ width: '100%' }}>
                <Space style={{ width: '100%', justifyContent: 'space-between' }}>
                  <Space size={4}>
                    <Text>{item.description}</Text>
                    {item.urgency && (
                      <Tag color={item.urgency.color} style={{ margin: 0 }}>{item.urgency.label}</Tag>
                    )}
                  </Space>
                  <Text strong>${(item.total_price ?? 0).toFixed(2)}</Text>
                </Space>
                <Text type="secondary" style={{ fontSize: 12 }}>
                  {item.labor_hours ? `${item.labor_hours}h` : ''}
                  {item.approval_status && ` · ${item.approval_status}`}
                </Text>
              </Space>
            </List.Item>
          )}
        />
        {(data.photos ?? []).length > 0 && (
          <div>
            <Text type="secondary" style={{ fontSize: 12, letterSpacing: 1 }}>PROGRESS PHOTOS</Text>
            <div style={{ display: 'flex', gap: 8, overflowX: 'auto', marginTop: 8 }}>
              {data.photos.map((p: any) => (
                <Image key={p.id} src={p.file_path} width={96} height={96} style={{ objectFit: 'cover', borderRadius: 4 }} />
              ))}
            </div>
          </div>
        )}
        <Text style={{ fontSize: 18 }}>
          <strong>Total:</strong> ${(wo.total_amount ?? 0).toFixed(2)}
        </Text>
      </Space>
    </PortalLayout>
  );
};

export default PortalWorkOrderPage;
